/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect } from 'react';
import './ExpandableProductCardGroup.scss';
import { Check, Copy, SquareArrowOutUpRight } from 'lucide-react';
import InfoButton from '../../components/serverDrivenUIComponents/InfoButton';
import { green } from 'sushiweb/tokens/color';
import { ToolTip } from '../../components/ComponentsV1/ToolTip/Tooltip';
import { SubstituteItemClickEvent } from '../../analytics/substitution-flow/events';
import { MetricsForJumboEvent } from '../../analytics/constants';

export const ExpandableProductCardGroup = ({
  props: {
    product_card_options: SubsItems = [],
    originally_ordered_product = {},
  } = {},
  setProductAttribute = () => {},
}) => {
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Auto-select the first *enabled* option on mount
  useEffect(() => {
    if (Array.isArray(SubsItems) && SubsItems.length > 0) {
      const firstEnabledIndex = SubsItems.findIndex(
        (opt) => opt.enabled !== false,
      );
      if (firstEnabledIndex !== -1) {
        setSelectedIndex(firstEnabledIndex);
        setProductAttribute(SubsItems[firstEnabledIndex].slug);
      }
    }
  }, [SubsItems, setProductAttribute]);

  const handleSelect = (index, slug, SubItem) => {
    SubstituteItemClickEvent({
      [MetricsForJumboEvent.feature_meta]: {
        data: SubItem ? JSON.stringify(SubItem) : '',
        position: index,
      },
    });

    if (index === selectedIndex) {
      setSelectedIndex(null);
      setProductAttribute(null);
    } else {
      setSelectedIndex(index);
      setProductAttribute(slug);
    }
  };

  if (!Array.isArray(SubsItems) || SubsItems.length === 0) return null;

  return (
    <div className='tw-flex-col tw-gap-5 tw-mt-4'>
      {originally_ordered_product && (
        <OriginallyOrderedProductCard {...originally_ordered_product} />
      )}

      {/* Substitute Items list */}
      <div className='tw-flex-col tw-gap-4 tw-border-solid !tw-border-y-0 !tw-border-r-0 tw-pl-4 tw-border-grey-300/50 tw-border-1'>
        {SubsItems.map((SubItem, index) => (
          <div
            key={SubItem.slug || index}
            className='product-card-option-group'
          >
            <div className='product-card-option-container'>
              <SubstituteProductCard
                {...SubItem}
                isSelected={index === selectedIndex}
                enabled={SubItem.enabled !== false}
                onSelect={() => handleSelect(index, SubItem.slug, SubItem)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Separate component for originally ordered product
const OriginallyOrderedProductCard = ({
  title = '',
  unit_of_measurement = '',
  price = '',
  quantity = '',
  image = '',
}) => (
  <div className='originally-ordered-product tw-rounded-xl tw-w-full tw-flex tw-border tw-border-solid tw-border-grey-200 tw-bg-grey-100 tw-overflow-hidden tw-flex-col tw-gap-4 tw-p-3'>
    <div className='tw-flex tw-w-full'>
      <div className='tw-w-[7rem] tw-h-[7rem] tw-border tw-border-solid tw-border-grey-200/50 tw-overflow-hidden tw-rounded-lg tw-mr-4'>
        <img src={image} alt={title} className='tw-h-[7rem] tw-w-[7rem]' />
      </div>
      <div className='tw-flex tw-flex-col tw-flex-1 tw-gap-1 tw-justify-between'>
        <div className='tw-font-medium tw-text-[1.3rem] tw-text-zinc-800 tw-leading-tight'>
          {title}
        </div>
        <div className='tw-flex tw-items-center tw-gap-2 tw-font-medium tw-text-[1.3rem] !tw-text-zinc-700 tw-flex-wrap'>
          {unit_of_measurement} | {quantity} X ₹{price}
        </div>
        <div className='tw-leading-none tw-p-[0.6rem] tw-text-[1.2rem] tw-font-medium tw-rounded-lg tw-bg-grey-200/80 tw-w-fit tw-text-zinc-600'>
          Originally ordered
        </div>
      </div>
    </div>
  </div>
);

// product card option component
const SubstituteProductCard = ({
  slug,
  title,
  unit,
  price,
  image,
  link,
  recommended_item: isRecommendedItem,
  enabled = true,
  isSelected = false,
  onSelect,
  children,
  tags,
}) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleClick = (e) => {
    e.stopPropagation();
    if (enabled) onSelect();
  };

  const handleKeyDown = (e) => {
    if (enabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      className={`tw-rounded-xl ${
        isSelected
          ? 'tw-ring-[0.15rem] tw-ring-green-700 !tw-border-none'
          : 'hover:tw-bg-grey-100/70 tw-transition-all tw-duration-300 tw-ease-in-out'
      } ${
        !enabled ? 'disabled' : ''
      } tw-w-full tw-flex tw-border tw-border-solid tw-border-grey-200 tw-flex-col tw-overflow-hidden tw-cursor-pointer`}
      role='button'
      aria-pressed={isSelected}
      aria-disabled={!enabled}
      tabIndex={enabled ? 0 : -1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className='tw-flex-col tw-w-full tw-gap-4 tw-p-3'>
        <div className='tw-flex tw-items-start tw-justify-between tw-w-ful tw-gap-2'>
          <div className='tw-flex tw-w-full'>
            <div className='tw-w-[6rem] tw-h-[6rem] tw-border tw-border-solid tw-border-grey-200/50 tw-overflow-hidden tw-rounded-lg tw-mr-4'>
              <img
                src={image}
                alt={title || slug}
                className='tw-h-[6rem] tw-w-[6rem]'
              />
            </div>

            <div className='tw-flex tw-flex-col tw-flex-1 tw-gap-1'>
              <div className='tw-font-medium tw-text-[1.3rem] tw-text-zinc-800 tw-leading-tight'>
                {title}
              </div>
              <div className='tw-flex tw-items-center tw-gap-2 tw-font-medium tw-text-[1.3rem] !tw-text-zinc-700'>
                {unit && (
                  <div className='tw-text-zinc-500 tw-text-[1.3rem]'>
                    {unit}
                  </div>
                )}{' '}
                {unit && '|'} <div className='tw-text-[1.3rem]'>₹{price}</div>
              </div>
            </div>
          </div>

          <ToolTip placement='top' content={'Copy product details'}>
            <span
              className='tw-flex tw-items-center tw-relative tw-w-6 tw-cursor-pointer'
              onClick={(e) => {
                e?.stopPropagation();
                setIsCopied(true);
                const productDetails = `${title}\n${
                  unit ? unit : ''
                } | ₹${price}\n\nProduct link: ${link ? link : 'N/A'}`;
                if (
                  navigator &&
                  navigator.clipboard &&
                  typeof navigator.clipboard.writeText === 'function'
                ) {
                  navigator.clipboard.writeText(productDetails);
                }
                setTimeout(() => {
                  setIsCopied(false);
                }, 1000);
              }}
            >
              <div>
                <Copy
                  size={14}
                  color={green.z700}
                  className={`tw-transition-all tw-absolute tw-duration-300 tw-ease-out ${
                    isCopied ? 'tw-opacity-0' : 'tw-opacity-100'
                  }`}
                />
              </div>
              <div>
                <Check
                  size={16}
                  color='#318616'
                  className={`tw-transition-all tw-absolute tw-duration-300 tw-ease-out ${
                    isCopied
                      ? 'tw-opacity-100 tw-scale-100'
                      : 'tw-opacity-0 tw-scale-75'
                  }`}
                />
              </div>
            </span>
          </ToolTip>

          {link && (
            <div onClick={(e) => e.stopPropagation()}>
              <a href={link} target='_blank' rel='noreferrer'>
                <SquareArrowOutUpRight
                  size={14}
                  className='tw-text-base-green tw-cursor-pointer'
                />
              </a>
            </div>
          )}
        </div>
        {/* Tags */}
        <div className='tw-flex tw-items-center tw-gap-2'>
          <TagsList tags={tags} />

          {isRecommendedItem && (
            <div className='tw-leading-none tw-p-[0.6rem] tw-text-[1.2rem] tw-font-medium tw-rounded-lg tw-bg-[#EDFAF1] tw-w-fit tw-text-base-green'>
              Recommended
            </div>
          )}
        </div>
      </div>

      {/* Resolution Data */}
      <div
        className={`
          tw-bg-blue-100 tw-flex-col
          tw-transition-all tw-duration-500 tw-ease-[cubic-bezier(.4,0,.2,1)]
          ${
            isSelected && children
              ? 'tw-max-h-[640px] tw-py-3 tw-px-4 tw-opacity-100'
              : 'tw-max-h-0 tw-py-0 tw-px-4 tw-opacity-0'
          }
          tw-overflow-hidden
        `}
        style={{
          transitionProperty: 'max-height, opacity, padding',
        }}
        aria-hidden={!isSelected}
      >
        {isSelected &&
          children &&
          children.map((child) => renderChildComponent(child))}
      </div>
    </div>
  );
};

const renderChildComponent = (child) => {
  if (!child || !child.type) return null;
  if (child.type === 'title') {
    return <Title details={child} />;
  }
  if (child.type === 'card') {
    return <Card details={child} />;
  }
  if (child.type === 'multiline_text') {
    return <MultilineText details={child} />;
  }
  return null;
};

const Title = ({ details }) => {
  return (
    <div
      className={
        'tw-flex tw-font-medium tw-text-[1.3rem] tw-leading-none tw-text-zinc-700'
      }
    >
      {details?.title}
      {details?.info?.length > 0 && <InfoButton details={details?.info} />}
    </div>
  );
};

const TagsList = ({ tags }) => {
  if (!Array.isArray(tags) || tags.length === 0) {
    return null;
  }
  return (
    <>
      {tags.map((tag) => (
        <div
          key={tag?.text}
          className='tw-leading-none tw-p-[0.6rem] tw-text-[1.2rem] tw-font-medium tw-rounded-lg tw-bg-blue-100 tw-w-fit tw-text-blue-600'
        >
          {tag?.text}
        </div>
      ))}
    </>
  );
};

const Card = (props) => {
  const { details } = props;
  return (
    <div className='tw-flex-col tw-gap-2 tw-mt-5'>
      {details?.children.length > 0 &&
        details?.children.map((child) => renderChildComponent(child))}
    </div>
  );
};

const MultilineText = ({ details }) => {
  return (
    <div className='tw-flex-col'>
      {(details?.lines || []).map((line, idx) => {
        const cleaned = line.replace(/[\u200B-\u200F\uFEFF]/g, '').trim();
        if (!cleaned) return null;

        return (
          <span
            key={idx}
            className='tw-font-medium tw-text-[1.2rem] tw-mt-2 tw-leading-tight tw-text-zinc-600'
          >
            {cleaned}
          </span>
        );
      })}
    </div>
  );
};
